import { z } from "zod";
import { Link as RouterLink } from "react-router-dom";
import { SubmitHandler, UseFormReturn, useWatch } from "react-hook-form";
import { useQueryParam } from "use-query-params";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { SafeNameString, SafeString } from "@packages/types";

import { createAddIssue } from "~/utils";
import { FormTextField } from "~/components/form/FormTextField";
import { useBackLink } from "~/components/core/BackLink";

export type SearchBoxFields = z.infer<(typeof SearchBox)["schema"]>;
type SearchType = SearchBoxFields["searchType"];

const SEARCH_TYPE_LABELS = {
  name: "applicant name",
  email: "applicant email",
  applicationId: "application ID (must be an exact match)",
  personId: "student ID (must be an exact match)",
} satisfies Record<SearchType, string>;

export function isSearchType(value: string): value is SearchType {
  return SearchBox.schema.safeParse({
    search: "",
    searchType: value,
  }).success;
}

export function useDashboardSearchType(defaultValue: SearchType) {
  return useQueryParam<SearchType>("searchType", {
    default: defaultValue,
    encode: (value) => value,
    decode: (value) =>
      (isSearchType(String(value)) ? String(value) : defaultValue) as SearchType,
  });
}

interface SearchBoxProps {
  form: UseFormReturn<SearchBoxFields>;
  onSearch: SubmitHandler<SearchBoxFields>;
}

export function SearchBox(props: SearchBoxProps) {
  const { control, handleSubmit } = props.form;
  const { onSearch } = props;

  const searchType = useWatch({ control, name: "searchType" });

  const backLink = useBackLink();

  return (
    <Stack component={Paper} p={3} boxShadow={2} gap={2}>
      <Box component="form" onSubmit={handleSubmit(onSearch)}>
        <FormTextField
          control={control}
          name="search"
          label={`Search by ${SEARCH_TYPE_LABELS[searchType]}`}
          helperText="Click on the buttons below to search by applicant name, email, student ID or application ID."
          variant="filled"
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton type="submit">
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Grid
          item
          component={Stack}
          alignItems="center"
          gap={1}
          display={{ xs: "none", md: "flex" }}
        >
          <Typography variant="body1" noWrap>
            Search by:
          </Typography>
          <SearchTypeChips form={props.form} />
        </Grid>
        <Grid item xs={12} md={3} container justifyContent="flex-end">
          <Button
            variant="contained"
            component={RouterLink}
            to="/applications/new"
            state={{ backLink }}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            New Application
          </Button>
        </Grid>
      </Stack>
    </Stack>
  );
}

function SearchTypeChips(props: Pick<SearchBoxProps, "form">) {
  const { control, setValue, clearErrors } = props.form;
  const searchType = useWatch({ control, name: "searchType" });

  const setSearchType = (value: typeof searchType) => {
    setValue("searchType", value);
    clearErrors("search");
  };

  return (
    <>
      <Chip
        label="Applicant name"
        color={searchType === "name" ? "primary" : "default"}
        onClick={() => setSearchType("name")}
      />
      <Chip
        label="Applicant email"
        color={searchType === "email" ? "primary" : "default"}
        onClick={() => setSearchType("email")}
      />
      <Chip
        label="Student ID"
        color={searchType === "personId" ? "primary" : "default"}
        onClick={() => setSearchType("personId")}
      />
      <Chip
        label="Application ID"
        color={searchType === "applicationId" ? "primary" : "default"}
        onClick={() => setSearchType("applicationId")}
      />
    </>
  );
}

SearchBox.schema = z
  .object({
    search: z.string().trim(),
    searchType: z.enum(["name", "email", "personId", "applicationId"]).default("name"),
  })
  .superRefine(({ search, searchType }, ctx) => {
    const addIssue = createAddIssue(ctx);
    const safeNameString = SafeNameString();
    if (searchType === "name" && !safeNameString.isValid(search))
      addIssue(safeNameString.defaultMessage, "search");
    if (searchType === "email" && !SafeString.isValid(search))
      addIssue(SafeString.defaultMessage, "search");
    if (searchType === "applicationId" && !isApplicationId(search))
      addIssue('Please enter a valid application ID starting with "A-"', "search");
    if (searchType === "personId" && !isPersonId(search))
      addIssue("Please enter a valid student ID", "search");
  });

function isApplicationId(value: string): value is `${"a" | "A"}-${number}` {
  return /^A-\d+$/i.test(value);
}

// Person ID should be a string of minimum length 7
function isPersonId(value: string): value is `${number}` {
  return /^[a-zA-Z0-9]{7,}$/i.test(value);
}
